import { useCallback, useEffect, useState } from 'react';

import {
  Box,
  Dialog,
  DialogContent,
  makeStyles,
  useMediaQuery,
} from '@material-ui/core';

import ModalSearchDrawer from 'components/ModalSearchDrawer';
import SearchQueryContext from 'contexts/SearchQueryContext';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoInterface,
  AtendimentoInterfaceV2,
  AtendimentoFilaInterface,
  setHistoricoAtendimentoNaFila,
} from 'store/reducers/atendimentos';
import { resetContatoAtendimentoV2 } from 'store/reducers/contatos';
import {
  resetMensagensAtendimentoNaFila,
  resetSearchQuery,
  fetchMensagensFinalizadasAtendimentoFila,
  setCursorAConsumir,
  resetAtendimentoFilaCursorAConsumir,
  resetPreviewMensagens,
  mensagensSelector,
} from 'store/reducers/mensagens';

import LeftGridDetalhesContato from './components/LeftGridDetalhesContato';
import RightGridMensagensAtendimento from './components/RightGridMensagensAtendimento';

const useStyles = makeStyles(({ breakpoints }) => ({
  dialogPaper: {
    height: '90%',
    width: '90%',
    overflow: 'hidden',
    [breakpoints.down('sm')]: {
      width: '100%',
      height: '100%',
    },
  },
  dialogContent: {
    padding: 0,
    margin: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '&:first-child': {
      paddingTop: 0,
    },
  },
  gridContainer: {
    height: '100%',
    width: '100%',
    margin: 0,
    display: 'flex',
    position: 'relative',
  },
}));

interface ModalHistoricoAtendimentoContatoProps {
  atendimento:
    | AtendimentoInterface
    | AtendimentoInterfaceV2
    | AtendimentoFilaInterface;
  open: boolean;
  onClose: () => void;
}

export default function ModalHistoricoAtendimentoContato({
  atendimento,
  open,
  onClose,
}: ModalHistoricoAtendimentoContatoProps) {
  const classes = useStyles();
  const isBrokenLayout = useMediaQuery('(max-width: 1080px)');
  const [openSearch, setOpenSearch] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  useEffect(() => {
    if (isBrokenLayout && openSearch) {
      setOpenSearch(false);
    }
  }, [isBrokenLayout, openSearch]);

  const dispatch = useAppDispatch();
  const { mensagensAtendimentoNaFila } = useAppSelector(mensagensSelector);

  const handleSelectMessage = useCallback(
    (messageId: string, afterCursor: string) => {
      const atendimentoId = atendimento?.id;

      if (atendimentoId) {
        // Se a mensagem já estiver na lista, não recarregamos, deixamos o NewMessagesContainer rolar até ela
        const isAlreadyMounted = mensagensAtendimentoNaFila.some(
          m => m.id === messageId,
        );

        if (isAlreadyMounted) return;

        dispatch(resetAtendimentoFilaCursorAConsumir());
        dispatch(resetMensagensAtendimentoNaFila());
        dispatch(
          fetchMensagensFinalizadasAtendimentoFila({
            atendimentoId,
            cursor: { afterCursor },
          }),
        )
          .unwrap()
          .then(data => {
            const { cursor } = data;
            dispatch(
              setCursorAConsumir({ ...cursor, type: 'atendimento-na-fila' }),
            );
          });
      }
    },
    [atendimento?.id, dispatch, mensagensAtendimentoNaFila],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      TransitionProps={{
        onEnter: () => {
          dispatch(setHistoricoAtendimentoNaFila(atendimento));
        },
        onExited: () => {
          dispatch(resetContatoAtendimentoV2());
          dispatch(resetMensagensAtendimentoNaFila());
          dispatch(setHistoricoAtendimentoNaFila(undefined));
          dispatch(resetPreviewMensagens());
          dispatch(resetSearchQuery());
          setLocalSearchQuery('');
          setOpenSearch(false);
        },
      }}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <DialogContent className={classes.dialogContent}>
        <SearchQueryContext.Provider value={localSearchQuery}>
          <Box className={classes.gridContainer}>
            <Box
              style={{
                position: 'relative',
                flex: '0 9999 500px',
                minWidth: '400px',
                display: isBrokenLayout ? 'none' : 'flex',
                flexDirection: 'column',
              }}
            >
              <LeftGridDetalhesContato
                atendimento={atendimento}
                onClose={onClose}
                isSmallScreen={isBrokenLayout}
                searchQuery={localSearchQuery}
                onSearchChange={setLocalSearchQuery}
              />
              <ModalSearchDrawer
                key={atendimento.id}
                atendimento={atendimento}
                open={openSearch}
                onOpen={() => setOpenSearch(true)}
                onClose={() => setOpenSearch(false)}
                onSelectMessage={handleSelectMessage}
                onSearchChange={setLocalSearchQuery}
                hideButton
              />
            </Box>

            {isBrokenLayout && (
              <Box flex={1}>
                <LeftGridDetalhesContato
                  atendimento={atendimento}
                  onClose={onClose}
                  isSmallScreen={isBrokenLayout}
                  searchQuery={localSearchQuery}
                  onSearchChange={setLocalSearchQuery}
                />
              </Box>
            )}

            {!isBrokenLayout && (
              <Box style={{ flex: '1 1 800px', minWidth: '400px' }}>
                <RightGridMensagensAtendimento
                  atendimentoId={atendimento.id}
                  contactName={
                    ('contato' in atendimento
                      ? atendimento.contato.nome
                      : atendimento.contact?.name) || ''
                  }
                  searchQuery={localSearchQuery}
                  openSearch={openSearch}
                  onToggleSearch={() => setOpenSearch(prev => !prev)}
                  onClose={onClose}
                />
              </Box>
            )}
          </Box>
        </SearchQueryContext.Provider>
      </DialogContent>
    </Dialog>
  );
}
